// src/components/PinisiKata/PinisiGameplayScreen.js
import React, { useState, useEffect, useMemo } from 'react'; // <-- Tambahkan 'useMemo'
import styles from './PinisiKata.module.css';
import { questionsDatabase } from '../../gameLogics/pinisiKataData';
import { ACTIONS } from './PinisiKataGame';

// --- 1. Komponen-Komponen Presentasional Kecil ---

const ProgressTrack = React.memo(({ player1Score, player2Score, totalQuestions }) => {
    const maxScore = totalQuestions * 125;
    const p1Progress = Math.min((player1Score / maxScore) * 100, 100);
    const p2Progress = Math.min((player2Score / maxScore) * 100, 100);

    return (
        <div className={styles.progressTrack}>
            {/* Player 1 Track */}
            <div className={styles.playerTrack}>
                <div className={`${styles.playerLabel} ${styles.player1Label}`}>🔴 TIM MERAH</div>
                <div className={styles.trackBar}>
                    <div className={styles.waterTrail} style={{ width: `${p1Progress}%` }}></div>
                    <div className={styles.ship} style={{ left: `${p1Progress}%` }}>⛵</div>
                </div>
                <div className={styles.scoreDisplay}>{player1Score}</div>
            </div>
            {/* Player 2 Track */}
            <div className={styles.playerTrack}>
                <div className={`${styles.playerLabel} ${styles.player2Label}`}>🔵 TIM BIRU</div>
                <div className={styles.trackBar}>
                    <div className={styles.waterTrail} style={{ width: `${p2Progress}%` }}></div>
                    <div className={styles.ship} style={{ left: `${p2Progress}%` }}>⛵</div>
                </div>
                <div className={styles.scoreDisplay}>{player2Score}</div>
            </div>
        </div>
    );
});

const QuestionArea = React.memo(({ question }) => {
    // Mengganti tag <blank> dengan span yang memiliki kelas
    const questionHTML = { __html: question.question.replace('<blank>', `<span class="${styles.blank}">`).replace('</blank>', '</span>') };
    
    return (
        <div className={styles.questionArea}>
            {/* SVG placeholder jika tidak ada gambar */}
            <img className={styles.questionImage} src={question.image || `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E`} alt="ilustrasi soal" />
            <div className={styles.questionText} dangerouslySetInnerHTML={questionHTML} />
        </div>
    );
});

const ChoicesPanel = React.memo(({ player, choices, onAnswer, disabled, answerStatus }) => {
    const isPlayer1 = player === 1;
    const panelStyle = isPlayer1 ? styles.player1Choices : styles.player2Choices;
    const title = isPlayer1 ? '🔴 TIM MERAH' : '🔵 TIM BIRU';

    return (
        <div className={`${styles.playerChoices} ${panelStyle}`}>
            <div className={styles.playerChoicesTitle}>{title}</div>
            {choices.map((choice, index) => {
                let buttonClass = styles.choiceButton;
                if (answerStatus && answerStatus.player === player && answerStatus.choiceIndex === index) {
                    buttonClass += answerStatus.isCorrect ? ` ${styles.correct}` : ` ${styles.incorrect}`;
                }

                return (
                    <button
                        key={index}
                        className={buttonClass}
                        onClick={() => onAnswer(player, index, choice)}
                        disabled={disabled}
                    >
                        {choice}
                    </button>
                );
            })}
        </div>
    );
});

// --- 2. Komponen Utama Gameplay ---

function PinisiGameplayScreen({ state, dispatch }) {
    // --- 1. PANGGIL SEMUA HOOKS DI LEVEL ATAS ---
    const { level, questionIndex, player1Score, player2Score } = state;
    const [answerStatus, setAnswerStatus] = useState(null);

    const questions = questionsDatabase[`level${level}`];
    const currentQuestion = questions[questionIndex];

    // PERBAIKAN: Gunakan `useMemo` untuk mengacak pilihan jawaban.
    // Kode di dalam `useMemo` hanya akan dijalankan kembali jika `currentQuestion` berubah.
    // Ini lebih efisien dan memperbaiki masalah urutan hook.
    const shuffledChoices1 = useMemo(() => {
        if (!currentQuestion) return [];
        return [...currentQuestion.choices].sort(() => Math.random() - 0.5);
    }, [currentQuestion]);

    const shuffledChoices2 = useMemo(() => {
        if (!currentQuestion) return [];
        return [...currentQuestion.choices].sort(() => Math.random() - 0.5);
    }, [currentQuestion]);

    // Hook `useEffect` sekarang juga berada di atas sebelum ada `return`
    useEffect(() => {
        if (answerStatus) {
            const timer = setTimeout(() => {
                setAnswerStatus(null);
                dispatch({ type: ACTIONS.NEXT_QUESTION });
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [answerStatus, dispatch]);
    
    // --- 2. EARLY RETURN SEKARANG DIPANGGIL SETELAH SEMUA HOOKS ---
    if (!currentQuestion) {
        // Ini akan terjadi di akhir permainan, sebelum layar pemenang muncul
        return <div className={`${styles.screen} ${styles.gameScreen} ${styles.active}`}>Memuat hasil...</div>;
    }

    // --- 3. LOGIKA DAN RENDER ---
    const handleAnswer = (player, choiceIndex, choice) => {
        if (answerStatus) return;

        const correctChoice = currentQuestion.choices[currentQuestion.correct];
        const isCorrect = choice === correctChoice;

        setAnswerStatus({ player, choiceIndex, isCorrect });
        dispatch({
            type: ACTIONS.SUBMIT_ANSWER,
            payload: { player, isCorrect }
        });
    };

    return (
        <div className={`${styles.screen} ${styles.gameScreen} ${styles.active}`}>
            <button 
                className={styles.backButton} 
                onClick={() => dispatch({ type: ACTIONS.GO_TO_SCREEN, payload: { screen: 'level' } })} 
                title="Pilih Level"
            >
                ⚙️
            </button>
            
            <ProgressTrack 
                player1Score={player1Score} 
                player2Score={player2Score}
                totalQuestions={questions.length}
            />

            <div className={styles.questionStatus}>
                SOAL KE {questionIndex + 1} DARI {questions.length}
            </div>

            <QuestionArea question={currentQuestion} />

            <div className={styles.choicesArea}>
                <ChoicesPanel 
                    player={1}
                    choices={shuffledChoices1}
                    onAnswer={handleAnswer}
                    disabled={!!answerStatus}
                    answerStatus={answerStatus}
                />
                <ChoicesPanel 
                    player={2}
                    choices={shuffledChoices2}
                    onAnswer={handleAnswer}
                    disabled={!!answerStatus}
                    answerStatus={answerStatus}
                />
            </div>
        </div>
    );
}
// Jangan lupa untuk menyalin kembali komponen kecil yang sudah benar
// const ProgressTrack = React.memo(...)
// const QuestionArea = React.memo(...)
// const ChoicesPanel = React.memo(...)

export default PinisiGameplayScreen;